import { useCallback, useEffect, useRef, useState } from "react";

import { useGlobalLoginSuccessManager } from "./useGlobalLoginSuccessManager";
import { useMemberUser } from "./useMemberUser";

/**
 * 登录流程处理 Hook
 * 业务方只需要注册自己的函数进来就行
 * 组件销毁时自动清理，只有当前存在的组件才会执行
 *
 * @param handler 登录成功后的处理函数
 * @param options 配置选项
 * @param options.waitForEquity 是否等待权益信息更新完成后再执行（默认 false）
 */
export const useLoginProcess = (
  handler: () => Promise<void> | void,
  options?: { waitForEquity?: boolean },
) => {
  const { register, unregister } = useGlobalLoginSuccessManager();
  const handlerRef = useRef(handler);
  const componentIdRef = useRef<string | null>(null);
  const isRegisteredRef = useRef(false); // 添加注册状态跟踪
  const [shouldExecute, setShouldExecute] = useState(false);

  // 如果需要等待权益更新，获取权益信息
  const { isLoading: equityLoading } = useMemberUser();

  const { waitForEquity = false } = options || {};

  // 更新 handler ref
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  // 创建稳定的处理器函数
  const stableHandler = useCallback(() => {
    if (waitForEquity) {
      // 如果需要等待权益更新，先标记为待执行
      setShouldExecute(true);
    } else {
      // 立即执行
      return handlerRef.current();
    }
  }, [waitForEquity]);

  // 监听权益加载状态，当加载完成且有待执行的操作时，执行处理函数
  useEffect(() => {
    if (waitForEquity && shouldExecute && !equityLoading) {
      setShouldExecute(false);
      handlerRef.current();
    }
  }, [waitForEquity, shouldExecute, equityLoading]);

  useEffect(() => {
    // 防止重复注册（React 严格模式下可能导致多次挂载）
    if (isRegisteredRef.current) {
      return;
    }

    // 生成唯一ID并注册
    const handlerName = handler.name || "anonymous";
    const componentId = `${handlerName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    componentIdRef.current = componentId;
    isRegisteredRef.current = true;

    register({
      id: componentId,
      handler: stableHandler,
    });

    // 组件销毁时自动清理
    return () => {
      if (componentIdRef.current && isRegisteredRef.current) {
        unregister(componentIdRef.current);
        componentIdRef.current = null;
        isRegisteredRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, unregister]); // 暂时移除 stableHandler 依赖
};
